import type { ReactNode } from "react";

/** Renders the small subset of Markdown used in lesson content — headings,
 * bullet/numbered lists, bold and italic — as styled elements instead of
 * showing the raw "###"/"**" syntax. Not a general-purpose parser. */
export function LessonContent({ text }: { text: string }) {
  const blocks = splitBlocks(text);

  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block, bi) => {
        const lines = block.split("\n").filter(Boolean);
        if (lines.length === 0) return null;

        const headingMatch = lines.length === 1 && lines[0].match(/^(#{1,4})\s+(.*)$/);
        if (headingMatch) {
          const level = headingMatch[1].length;
          const label = parseInline(headingMatch[2], `h-${bi}`);
          if (level <= 2) {
            return (
              <h3 key={bi} className="mt-5 border-b-2 border-brand-100 pb-2 text-base font-bold text-slate-900 first:mt-0">
                {label}
              </h3>
            );
          }
          return (
            <h4
              key={bi}
              className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-md bg-brand-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-brand-700 first:mt-0"
            >
              {label}
            </h4>
          );
        }

        if (lines.every(isListLine)) {
          const numbered = isNumberedLine(lines[0]);
          return (
            <ul
              key={bi}
              className={`flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 ${numbered ? "list-decimal pl-8" : ""}`}
            >
              {lines.map((line, li) => (
                <li key={li} className="flex gap-2 text-sm leading-relaxed text-slate-700">
                  {!numbered && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-400" />}
                  <span>{parseInline(stripMarker(line), `li-${bi}-${li}`)}</span>
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={bi} className="text-sm leading-relaxed text-slate-700">
            {parseInline(lines.join(" "), `p-${bi}`)}
          </p>
        );
      })}
    </div>
  );
}

/** Splits lesson text into blocks on blank lines, and also always breaks a
 * heading line into its own block — even when the author didn't put a blank
 * line around it — so it's never swallowed into an adjacent paragraph. */
function splitBlocks(text: string): string[] {
  const blocks: string[] = [];
  let current: string[] = [];

  const flush = () => {
    if (current.length > 0) {
      blocks.push(current.join("\n"));
      current = [];
    }
  };

  for (const rawLine of text.split("\n")) {
    const line = rawLine.trim();
    if (line === "") {
      flush();
      continue;
    }
    if (/^#{1,4}\s+/.test(line)) {
      flush();
      blocks.push(line);
      continue;
    }
    current.push(line);
  }
  flush();

  return blocks;
}

function isListLine(line: string) {
  return /^[-*]\s+/.test(line) || /^\d+\.\s+/.test(line);
}

function isNumberedLine(line: string) {
  return /^\d+\.\s+/.test(line);
}

function stripMarker(line: string) {
  return line.replace(/^[-*]\s+/, "").replace(/^\d+\.\s+/, "");
}

function parseInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let key = 0;

  for (const part of text.split(/(\*\*.+?\*\*)/g)) {
    if (!part) continue;
    if (/^\*\*.+\*\*$/.test(part)) {
      nodes.push(
        <strong key={`${keyPrefix}-${key++}`} className="font-semibold text-slate-900">
          {part.slice(2, -2)}
        </strong>,
      );
      continue;
    }
    for (const sub of part.split(/(\*[^*\s][^*]*\*)/g)) {
      if (!sub) continue;
      if (/^\*[^*\s][^*]*\*$/.test(sub)) {
        nodes.push(
          <em key={`${keyPrefix}-${key++}`} className="text-slate-600">
            {sub.slice(1, -1)}
          </em>,
        );
      } else {
        nodes.push(<span key={`${keyPrefix}-${key++}`}>{sub}</span>);
      }
    }
  }
  return nodes;
}
